import React, {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useState,
} from 'react'
import { useLocation } from 'react-router-dom'
import log from 'loglevel'
import { UserPromiseClient } from '@trustero/trustero-api-web/lib/account/account_grpc_web_pb'
import { GetRequest } from '@trustero/trustero-api-web/lib/account/account_pb'
import { GoogleAnalyticsService } from '../analytics/googleAnalyticsService'
import { EnvConfig } from '../config/config'
import { isEuProd } from '../Utils/environment'
import { useAuthorizedGrpcClient } from '../adapter'
import { AuthContext } from './authContext'
import { useConsent } from './ConsentContext'

const logger = log.getLogger('context')

export const AnalyticsContext = createContext<{
  analyticsService?: GoogleAnalyticsService
}>({
  analyticsService: undefined,
})

interface AnalyticsProviderProps {
  children: JSX.Element
}

export const AnalyticsProvider = ({
  children,
}: AnalyticsProviderProps): JSX.Element => {
  const measurementId = EnvConfig.gaMeasurementId
  const { consentStatus } = useConsent()
  const canLoad = !isEuProd() || consentStatus === 'accepted'
  const [analyticsService, setService] = useState<GoogleAnalyticsService>()
  const location = useLocation()
  const { authCtx } = useContext(AuthContext)
  const UserClient = useAuthorizedGrpcClient(UserPromiseClient)
  const isSignedIn = authCtx.isAuthenticated
  const [wasSignedIn, toggleSignedOut] = useReducer(
    (p: boolean) => !p,
    isSignedIn,
  )

  // init service
  useEffect(() => {
    if (!measurementId || !canLoad) {
      if (analyticsService) {
        analyticsService.disable()
        setService(undefined)
      }
      return
    }
    if (analyticsService) {
      return
    }

    const newService = new GoogleAnalyticsService(measurementId)
    newService.init()
    setService(newService)
  }, [analyticsService, measurementId, canLoad])

  // Update userId when user signs in
  useEffect(() => {
    if (!analyticsService || !canLoad) {
      return
    }
    if ((wasSignedIn && isSignedIn) || (!wasSignedIn && !isSignedIn)) {
      return
    }
    // User/Get needs the permissions login is still loading; calling now is
    // refused, and the toggle below would mean it never retries.
    if (isSignedIn && authCtx.isAuthLoading) {
      return
    }
    const updateUserId = async () => {
      try {
        const user = await UserClient.get(
          new GetRequest().setEmail(authCtx.email),
        )
        if (user) analyticsService?.updateUser(user.getId())
      } catch (exception) {
        logger.error('error fetching user', exception)
      }
      toggleSignedOut()
    }
    updateUserId()
  }, [
    wasSignedIn,
    isSignedIn,
    analyticsService,
    canLoad,
    UserClient,
    authCtx.email,
    authCtx.isAuthLoading,
  ])

  useEffect(() => {
    if (!canLoad) {
      return
    }
    analyticsService?.pageView(`${location.pathname}${location.search}`)
  }, [analyticsService, canLoad, location.pathname, location.search])

  if (!measurementId || !canLoad) {
    return <>{children}</>
  }

  return (
    <AnalyticsContext.Provider value={{ analyticsService }}>
      {children}
    </AnalyticsContext.Provider>
  )
}
